import { gql } from '@apollo/client';

import { EventRequest, Quote, Event, Supplier } from '../../types';
import { Attachment, QuoteLineItemsFields } from './quoteForRequest';
import {
  priceBreakdown,
  quotePriceBreakdown,
} from '../../graphql/fragments/quotes/priceBreakdown';
import { VatStatusType } from '../../constants/quotes';
import { QuotePriceDetails } from '../../types/quote';

type QuoteFields = Pick<
  Quote,
  | 'uuid'
  | 'state'
  | 'total'
  | 'service_fee'
  | 'covers'
  | 'supportText'
  | 'old_commission'
  | 'protection_type'
  | 'protection_fee'
  | 'protection_rounding'
  | 'commission_fee'
  | 'read_timestamp'
  | 'priceBreakdown'
  | 'vatStatus'
>;

type SupplierFields = Pick<
  Supplier,
  | 'uuid'
  | 'name'
  | 'category_tier1'
  | 'images'
  | 'tagline'
  | 'average_rating'
  | 'slug'
  | 'vat_number'
  | 'muta_accredited'
>;

type EventRequestFields = Pick<
  EventRequest,
  | 'uuid'
  | 'id'
  | 'tags'
  | 'close_date'
  | 'completed'
  | 'category_tier2'
  | 'description'
  | 'budget'
  | 'item_quantity'
  | 'additional_info'
  | 'categories_tier2'
  | 'event_request_tags'
>;

type EventFields = Pick<
  Event,
  | 'id'
  | 'uuid'
  | 'type'
  | 'date'
  | 'location_line_1'
  | 'location_line_2'
  | 'postcode'
  | 'location_city'
  | 'start_time'
  | 'end_time'
  | 'user_id'
  | 'custom_event_name'
>;

type QuotePriceDetailsFields = {
  uuid: string;
  quoteUuid: string;
  supplierTakeHomeAmount: number;
  commissionFeeRate: number;
  commissionFeeAmount: number;
  quoteTotalAmount: number;
  serviceFeeRate: number;
  serviceFeeAmount: number;
  protectPlusRate: number;
  protectPlusAmount: number;
  partnerCommissionRate: number;
  partnerCommissionAmount: number;
  affiliateCommissionRate: number;
  affiliateCommissionAmount: number;
  combinedServiceFeeRate: number;
  combinedServiceFeeAmount: number;
  customerTotalPriceExclVat: number;
  vatAmount: number;
  customerTotalAmount: number;
  created: string;
  updated: string;
};

export type QuoteByUuid = QuoteFields & {
  quote_line_items: Array<QuoteLineItemsFields>;
  suppliers: SupplierFields;
  is_excl_vat_price?: boolean;
  event_requests: EventRequestFields & {
    events: EventFields & {
      event_requests: Pick<EventRequest, 'category_tier2'> & {
        quotes: Array<Pick<Quote, 'uuid'>>;
      };
    };
  };
  quotePriceDetails: QuotePriceDetailsFields;
  attachments: Attachment[];
};

export interface QuotesByUuid<T = QuoteByUuid> {
  quotes: T[];
}

export type QuotePriceDetailsByUuid = {
  quote_line_items: Array<Omit<QuoteLineItemsFields, 'priceBreakdown'>>;
  vatStatus: VatStatusType;
  quotePriceDetails: Pick<
    QuotePriceDetails,
    | 'commissionFeeRate'
    | 'quoteTotalAmount'
    | 'serviceFeeRate'
    | 'partnerCommissionRate'
    | 'combinedServiceFeeRate'
    | 'vatAmount'
    | 'protectPlusRate'
    | 'protectPlusAmount'
    | 'quoteTotalExclVatAmount'
  >;
  suppliers: Pick<Supplier, 'vat_number'>;
};

export const QUOTE_PRICE_DETAILS_BY_UUID = gql`
  query quotes($quoteUuid: String!) {
    quotes(where: { uuid: { equals: $quoteUuid } }) {
      quote_line_items {
        title
        description
        quantity
        price
        order
      }
      vatStatus
      suppliers {
        vat_number
      }
      quotePriceDetails {
        commissionFeeRate
        quoteTotalAmount
        serviceFeeRate
        partnerCommissionRate
        combinedServiceFeeRate
        vatAmount
        protectPlusAmount
        protectPlusRate
        quoteTotalExclVatAmount
      }
    }
  }
`;

export const QUOTE_BY_UUID = gql`
  query quotes($quoteUuid: String!) {
    quotes(where: { uuid: { equals: $quoteUuid } }) {
      uuid
      state
      total
      vatStatus
      service_fee
      covers
      supportText
      old_commission
      protection_type
      protection_fee
      protection_rounding
      commission_fee
      read_timestamp
      quote_line_items {
        title
        description
        quantity
        price
        order
        priceType
        image {
          url
        }
        priceBreakdown {
          ...PriceBreakdown
        }
      }
      suppliers {
        uuid
        name
        category_tier1
        images
        tagline
        average_rating
        slug
        vat_number
        muta_accredited
        alcohol_permit
        supplierPermissions {
          permissionType
        }
      }
      event_requests {
        id
        uuid
        tags
        category_tier2
        description
        budget
        item_quantity
        close_date
        completed
        additional_info {
          courses
          children
          waiters
          water
          crockery
          power
          bar_type
          supplier_setup
        }
        categories_tier2 {
          name
          parent
        }
        event_request_tags {
          tags_event_request_tags_tag_uuidTotags {
            name
            uuid
            root_tags {
              key
            }
          }
        }
        quotes {
          uuid
        }
        events {
          id
          uuid
          user_id
          type
          custom_event_name
          date
          date_flexible
          dateFlexibilityNotes
          location_line_1
          location_line_2
          postcode
          location_city
          start_time
          end_time
          users {
            user_preferences {
              showCustomerPhoneNumber
            }
          }
          event_requests {
            category_tier2
          }
        }
      }
      priceBreakdown {
        ...QuotePriceBreakdown
      }
      quotePriceDetails {
        uuid
        quoteUuid
        supplierTakeHomeAmount
        commissionFeeRate
        commissionFeeAmount
        quoteTotalAmount
        serviceFeeRate
        serviceFeeAmount
        protectPlusRate
        protectPlusAmount
        partnerCommissionRate
        partnerCommissionAmount
        affiliateCommissionRate
        affiliateCommissionAmount
        combinedServiceFeeRate
        combinedServiceFeeAmount
        vatAmount
        customerTotalAmount
        customerTotalPriceExclVat
        created
        updated
      }
      is_excl_vat_price
      vatStatus
      attachments {
        filename
        key
        url
      }
    }
  }
  ${quotePriceBreakdown}
  ${priceBreakdown}
`;

export type MessagesQuotePriceDetails = {
  uuid: string;
  state?: string;
  total?: number;
  vatStatus?: VatStatusType;
  service_fee?: number;
  old_commission?: number;
  protection_type?: string;
  protection_fee?: number;
  protection_rounding?: number;
  commission_fee?: number;
  read_timestamp?: string;
  quotePriceDetails?: Pick<
    QuotePriceDetails,
    | 'quoteUuid'
    | 'supplierTakeHomeAmount'
    | 'commissionFeeRate'
    | 'commissionFeeAmount'
    | 'quoteTotalAmount'
    | 'serviceFeeRate'
    | 'serviceFeeAmount'
    | 'protectPlusRate'
    | 'protectPlusAmount'
    | 'partnerCommissionRate'
    | 'partnerCommissionAmount'
    | 'affiliateCommissionRate'
    | 'affiliateCommissionAmount'
    | 'combinedServiceFeeRate'
    | 'combinedServiceFeeAmount'
    | 'vatAmount'
    | 'customerTotalAmount'
    | 'customerTotalPriceExclVat'
  > & {
    uuid?: string;
    created?: string;
    updated?: string;
  };
};

export const MESSAGES_QUOTE_PRICE_DETAILS_BY_UUID = gql`
  query quotes($quoteUuid: String!) {
    quotes(where: { uuid: { equals: $quoteUuid } }) {
      uuid
      state
      total
      vatStatus
      service_fee
      old_commission
      protection_type
      protection_fee
      protection_rounding
      commission_fee
      read_timestamp
      quotePriceDetails {
        uuid
        quoteUuid
        supplierTakeHomeAmount
        commissionFeeRate
        commissionFeeAmount
        quoteTotalAmount
        serviceFeeRate
        serviceFeeAmount
        protectPlusRate
        protectPlusAmount
        partnerCommissionRate
        partnerCommissionAmount
        affiliateCommissionRate
        affiliateCommissionAmount
        combinedServiceFeeRate
        combinedServiceFeeAmount
        vatAmount
        customerTotalAmount
        customerTotalPriceExclVat
        created
        updated
      }
      is_excl_vat_price
      vatStatus
    }
  }
`;
